<script setup lang="ts">
    import { Content } from "@builder.io/sdk-vue";
    import { reactive, type Component, computed, watch, toRefs, ref, onMounted, nextTick } from "vue";
    import BuilderApi from "@/helpers/api/Builder";
    import useBuilder from "@/helpers/composables/builder";
    import HeroBanner from "@/components/HeroBanner.vue";
    interface BaseBuilderIoInputInterface {
        name: String;
        component: Component;
    }

    interface BooleanBuilderIoInputInterface
        extends BaseBuilderIoInputInterface {
        type: "boolean";
        defaultValue: false;
    }

    let props = defineProps({
        contentProp: {
            type: String,
            default: null
        },
        readableFields: {
            type: Array<string>,
            default: () => []
        },
        model: {
            type: String,
            required: true
        },
        components: {
            type: Array<Object>,
            default: () => []
        },
        driver: {
            type: String,
            default: "test",
            validator: (value: string) => ["test", "builder"].includes(value)
        },
        url: {
            type: String,
            default: "",
            required: false
        },
        apiKey: {
            type: String,
            default: import.meta.env.VITE_BUILDER_IO_API_KEY
        },
        isData: {
            type: Boolean,
            default: false
        },
        isSection: {
            type: Boolean,
            default: false
        },
        injectedData: {
            type: Object,
            default: () => ({})
        },
        injectedContext: {
            type: Object,
            default: () => ({})
        },
        isPage: {
            type: Boolean,
            default: false
        },
        fallBackUrl: {
            type: String,
            default: ""
        },
        searchParams: {
            type: Object,
            default: () => ({})
        },
        limit: {
            type: Number,
            default: 30
        },
    });

    if (props.model == null) {
        throw new Error('Missing required prop: "model"');
    }

    let isBuilderPage = computed(() => {
        return (
            props.driver == "builder" && (props.model == "page" || props.isPage)
        );
    });

    let isBuilderSection = computed(() => {
        return props.driver == "builder" && props.isSection;
    });

    let isBuilderData = computed(() => {
        return props.driver == "builder" && props.isData == true;
    });
    // if(isBuilderData.value){
    let builderData = defineModel();
    // }

    let lazy = defineModel("lazy", {
        type: Boolean,
        default: false
    });

    if (isBuilderPage.value) {
        if (props.url == null) {
            throw new Error('Missing required prop: "url"');
        }
    }

    let builder = reactive(
        new BuilderApi(
            import.meta.env.VITE_CMS_DRIVER,
            import.meta.env.VITE_BUILDER_IO_API_KEY
        )
    );

    let {searchParams, limit} = toRefs(props);

    let { state, data, isLoadingContent, fetchBuilderContent } = useBuilder({
        builder,
        isBuilderPage: isBuilderPage.value,
        isBuilderSection: isBuilderSection.value,
        isBuilderData: isBuilderData.value,
        url: props.url,
        fallBackUrl: props.fallBackUrl,
        model: props.model,
        readableFields: props.readableFields,
        search: searchParams,
        limit,
        data: builderData,
        lazy: lazy,
        contentProp: props.contentProp,
    });

    defineExpose({
        hasBuilderContent: state.canShowContent,
        isLoadingContent: isLoadingContent
    });
    

    watch(data, (newData) => {
        builderData.value = newData;

        nextTick(()=>{
            let images = document.querySelectorAll('img');
            images.forEach((image) => {
                if(image.src.includes("https://cdn.builder.io/api/v1/pixel")){
                    image.setAttribute('aria-hidden', 'true');
                }
            });
        })
    });

    const bannerDefaults = computed(()=>{
        let defaults = {
            banner_media: {
                min_height: '300',
                max_height: '520',
            },
            banner_content: {
                text_bg_color: 'None',
                content_align_vertical: 'middle',
                content_align_horizontal: 'center'
            },
            eyebrow: {
                text: '',
                color: 'rothcoBlack'
            },
            heading: {
                text: state.content.error,
                color: 'rothcoBlack'
            },
        };
        return defaults;
    })
</script>
<template>
    <div v-if="isBuilderPage || isBuilderSection && state.canShowContent" data-allow-mismatch>
        <Content
            v-if="!state?.content?.error"
            :context="injectedContext"
            :data="injectedData"
            :model="model"
            :content="state.content"
            :api-key="apiKey"
            :customComponents="components"
        />
        <HeroBanner v-bind="bannerDefaults" v-else></HeroBanner>
    </div>
    <div data-allow-mismatch v-else-if="isBuilderData">
        <div>
            <slot></slot>
        </div>
    </div>
    <div data-allow-mismatch v-else>
        <div>
        </div>
    </div>
</template>
